import { useEffect, useRef, useState } from "react";
import { Pause, Play, Square, Volume2, VolumeX, Waves } from "lucide-react";
import { BoxBreath, boxPhase } from "@/components/landing/BoxBreath";
import { GROUND_TOTAL, Grounding } from "@/components/landing/Grounding";
import { CalmFeedback } from "@/components/landing/CalmFeedback";
import { chime, startWaves } from "@/components/landing/calmSound";

const LENGTH = 60000;
const R = 26, C = 2 * Math.PI * R;
export const SOUNDS = [["off", "Sound off", VolumeX], ["chime", "Chime", Volume2], ["waves", "Waves", Waves]];
const reduced = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export const Ring = ({ elapsed, length = LENGTH }) => (
  <div className="relative w-16 h-16" role="progressbar" aria-valuemin={0} aria-valuemax={Math.round(length / 1000)} aria-valuenow={Math.floor(elapsed / 1000)} aria-label="Time" data-testid="calm-ring">
    <svg viewBox="0 0 60 60" className="w-16 h-16 -rotate-90"><circle cx="30" cy="30" r={R} fill="none" stroke="var(--mm-primary)" strokeOpacity="0.15" strokeWidth="5" />
      <circle cx="30" cy="30" r={R} fill="none" stroke="var(--mm-primary)" strokeWidth="5" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - elapsed / length)} /></svg>
    <span className="absolute inset-0 flex items-center justify-center text-sm font-bold tabular-nums" data-testid="calm-seconds-left">{Math.ceil((length - elapsed) / 1000)}s</span>
  </div>
);

export const useClock = (running, onEnd, length = LENGTH) => {
  const [elapsed, setElapsed] = useState(0);
  const end = useRef(onEnd);
  end.current = onEnd;
  useEffect(() => {
    if (!running) return undefined;
    let raf, last = performance.now();
    const tick = (now) => {
      const dt = Math.max(0, now - last);
      last = now;
      setElapsed((e) => { const n = Math.min(length, e + dt); if (n >= length) setTimeout(() => end.current(), 0); return n; });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [running, length]);
  return [elapsed, setElapsed];
};

export const CalmTry = () => {
  const [mode, setMode] = useState(null);
  const [stage, setStage] = useState("pick");
  const [sound, setSound] = useState("off");
  const [taps, setTaps] = useState(0);
  const [feel, setFeel] = useState(null);
  const finish = () => setStage("done");
  const [elapsed, setElapsed] = useClock(stage === "run", finish);
  const lastPhase = useRef(-1);

  useEffect(() => {
    if (stage !== "run" || sound !== "waves") return undefined;
    return startWaves();
  }, [stage, sound]);
  useEffect(() => {
    const ph = boxPhase(elapsed);
    if (mode === "box" && stage === "run" && sound === "chime" && ph !== lastPhase.current) chime(ph % 4 === 2 ? 440 : 528);
    lastPhase.current = ph;
  }, [elapsed, mode, stage, sound]);

  const begin = (m) => { setMode(m); setTaps(0); setFeel(null); setElapsed(0); lastPhase.current = -1; setStage("run"); };
  const doneTimer = useRef(null);
  useEffect(() => () => clearTimeout(doneTimer.current), []);
  const again = () => { clearTimeout(doneTimer.current); setStage("pick"); setFeel(null); setElapsed(0); };
  const tap = () => { const n = taps + 1; setTaps(n); if (sound === "chime") chime(600); if (n >= GROUND_TOTAL) doneTimer.current = setTimeout(finish, 1200); };

  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 mt-16" data-testid="calm-try">
      <div className="rounded-[32px] p-6 sm:p-10 bg-gradient-to-br from-[#F3EEFC] to-[#E8F4F8] border border-mm-primary/10">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <span className="mm-chip !cursor-default !text-xs">Free · No sign-up</span>
            <h2 className="text-2xl sm:text-3xl font-bold mt-3">Try a 60-second calm</h2>
            <p className="text-mm-muted mt-1">One minute, right here. See how it feels.</p>
          </div>
          <div className="flex gap-1 rounded-full bg-white/70 p-1" role="radiogroup" aria-label="Sound">
            {SOUNDS.map(([k, l, I]) => (
              <button key={k} role="radio" aria-checked={sound === k} onClick={() => setSound(k)} className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${sound === k ? "bg-mm-primary text-white" : "text-mm-muted hover:text-mm-primary"}`} data-testid={`calm-sound-${k}`}><I size={14} />{l}</button>
            ))}
          </div>
        </div>

        <div className="mt-8 min-h-[280px] flex flex-col justify-center">
          {stage === "pick" && (
            <div className="grid sm:grid-cols-2 gap-4">
              <button onClick={() => begin("box")} className="mm-card mm-card-hover p-6 text-left" data-testid="calm-pick-box"><div className="text-3xl" aria-hidden>🟪</div><h3 className="font-bold text-lg mt-3">Box breathing</h3><p className="text-sm text-mm-muted mt-1">Breathe in, hold, out, hold. Four seconds each.</p></button>
              <button onClick={() => begin("ground")} className="mm-card mm-card-hover p-6 text-left" data-testid="calm-pick-ground"><div className="text-3xl" aria-hidden>🌿</div><h3 className="font-bold text-lg mt-3">5-4-3-2-1 grounding</h3><p className="text-sm text-mm-muted mt-1">Use your senses to come back to the present.</p></button>
            </div>
          )}
          {(stage === "run" || stage === "paused") && (
            <div className="grid sm:grid-cols-[1fr_auto] gap-6 items-center">
              {mode === "box" ? <BoxBreath elapsed={elapsed} still={reduced()} /> : <Grounding taps={taps} onTap={tap} />}
              <div className="flex sm:flex-col items-center justify-center gap-3">
                <Ring elapsed={elapsed} />
                <button onClick={() => setStage(stage === "run" ? "paused" : "run")} className="mm-btn-ghost !px-4 !py-2" data-testid="calm-pause">{stage === "run" ? <><Pause size={16} />Pause</> : <><Play size={16} />Resume</>}</button>
                <button onClick={again} className="mm-btn-ghost !px-4 !py-2" data-testid="calm-stop"><Square size={14} />Stop</button>
              </div>
            </div>
          )}
          {stage === "done" && <CalmFeedback feel={feel} onFeel={setFeel} onAgain={again} />}
        </div>
      </div>
    </section>
  );
};
